// @givmo/passkeys — the Sign-in & security card's notice for a pending reset (AS-WEB13): where the web
// cancels one. The apps' Today alert, word for word (D-AS-29 decisions 2, 3 and 5; ruled for cut
// 1003c): "Passkey reset requested" or, on an account with an authenticator app, "Two-factor reset
// requested"; the deadline as decision 3 says it; and "Wasn't me — cancel it", the label every email,
// notification and screen names.
//
// It reads the account's own security read (`mfa.pending_factor_reset`), so it shows wherever a reset
// is armed, whoever armed it, and is gone once the read says so. The cancel is the authenticated
// `DELETE /users/me/security/factor-reset` (idempotent: a double click or a stale card is not a
// failure); its answer replaces the read, and the card then says what stayed — only while the account
// still reads as the cancel left it: a later change (a recovery code removing the authenticator in the
// same visit, AS-WEB18) would make "Two-factor authentication will stay on." false.
//
// ⚠ THE CANCEL IS A NARROW ROUTE (AS-WEB23): its answer leaves `sign_in_lock`, `employer_sso` and
// `employer_policy` null, "not stated". It replaces the read through `recordFactorResetCancelled`,
// which keeps the last full read's statement of each, so a lock-bound card stays locked after the
// cancel instead of offering every button its lock refuses (cut 1009b's review, F1).
//
// FINISHING (AS-WEB17): once the wait has run, the notice also offers "Finish the reset" (APPROVED,
// D-AS-39 E8), which the card opens as the reset's finish — so an account with no passkey, whose
// only other door is a prompt's "Lost your authenticator?", finishes from here — and its line says
// "It can be finished now…" (APPROVED, D-AS-39 E8) rather than a date in the past. Before the
// deadline nothing new shows; a page left open across it re-renders then. The deadline is the
// server's, never counted down, and the server decides again when the finishing code arrives.

import { useEffect, useState } from 'react';
import type { StepUpLostFactor } from '@givmo/step-up';
import { useQueryClient } from '@tanstack/react-query';
import { Button, ErrorMessage, testIdProp } from '@givmo/design-system';
import type { UserSecurityOut } from '@givmo/api-client';
import { recordFactorResetCancelled, servedData, useMySecurity } from './account-factors';
import { pendingFactorResetWords } from './factor-reset-copy';
import { cancelFactorReset } from './factor-reset-transport';

const PREFIX = 'sign-in-security-reset';

/** The longest a timer can wait in a browser (2^31 − 1 ms, about 24.8 days). */
const LONGEST_TIMER_MS = 2_147_483_647;

/**
 * Whether `executesAt` has passed, re-rendering once when it does: a card left open across the
 * deadline offers the finish without a reload.
 */
function usePassed(executesAt: Date | null): boolean {
  const [now, setNow] = useState(() => Date.now());
  const at = executesAt?.getTime() ?? null;
  useEffect(() => {
    if (at === null) return undefined;
    const wait = at - Date.now();
    if (wait <= 0 || wait > LONGEST_TIMER_MS) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), wait + 1);
    return () => clearTimeout(timer);
  }, [at]);
  return at !== null && at <= Math.max(now, Date.now());
}

/** The pending reset's deadline from a served read, or `null` when none is armed (or unreadable). */
export function pendingFactorResetOf(security: UserSecurityOut | undefined): Date | null {
  const executesAt = (
    security as { mfa?: { pending_factor_reset?: { executes_at?: unknown } | null } } | undefined
  )?.mfa?.pending_factor_reset?.executes_at;
  if (typeof executesAt !== 'string' || executesAt.length === 0) return null;
  const date = new Date(executesAt);
  return Number.isNaN(date.getTime()) ? null : date;
}

export interface PendingFactorResetNoticeProps {
  uid: string | null;
  /**
   * Open the reset's finish for the factor the notice names (AS-WEB17); offered once the wait has
   * run. Absent (the console supplies no reset here), the notice offers the cancel alone.
   */
  onFinish?: (factor: StepUpLostFactor) => void;
}

export function PendingFactorResetNotice({ uid, onFinish }: PendingFactorResetNoticeProps) {
  const queryClient = useQueryClient();
  const security = servedData(useMySecurity(uid));
  const [cancelling, setCancelling] = useState(false);
  const [failed, setFailed] = useState(false);
  /**
   * What stayed, said once the cancel went through, with the read the cancel left: it is said only
   * while the account still reads that way (the cache keeps the same object while nothing changed).
   */
  const [cancelled, setCancelled] = useState<{ line: string; read: unknown } | null>(null);

  const executesAt = pendingFactorResetOf(security);
  const holdsAuthenticator = security?.mfa?.totp_enrolled === true;
  const words = pendingFactorResetWords(holdsAuthenticator);
  const waitIsOver = usePassed(executesAt);

  if (executesAt === null) {
    if (cancelled === null || cancelled.read !== security) return null;
    return (
      <div className="px-20 py-16">
        <p
          role="status"
          className="font-sans text-s14 font-w600 tracking-givmo text-success-700"
          {...testIdProp(`${PREFIX}-cancelled`)}
        >
          {cancelled.line}
        </p>
      </div>
    );
  }

  const cancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    setFailed(false);
    try {
      const read = recordFactorResetCancelled(queryClient, uid, await cancelFactorReset());
      setCancelled({ line: words.cancelled, read });
    } catch {
      setFailed(true);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="px-20 py-16">
      <div
        className="flex flex-col items-start gap-10 rounded-lg border border-warning-100 bg-warning-50 p-16"
        {...testIdProp(`${PREFIX}-notice`)}
      >
        <p className="font-sans text-s14 font-w600 tracking-givmo text-warning-700">
          {words.headline}
        </p>
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-warning-700"
          {...testIdProp(`${PREFIX}-notice-body`)}
        >
          {waitIsOver ? words.dueBody : words.body(executesAt)}
        </p>
        {failed && (
          <ErrorMessage className="pt-0" testId={`${PREFIX}-cancel-failed`}>
            That didn’t work. Try again.
          </ErrorMessage>
        )}
        <div className="flex flex-wrap gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            loading={cancelling}
            onClick={() => void cancel()}
            testId={`${PREFIX}-cancel`}
          >
            {words.cancel}
          </Button>
          {waitIsOver && onFinish !== undefined && (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              disabled={cancelling}
              onClick={() => onFinish(holdsAuthenticator ? 'authenticator' : 'passkey')}
              testId={`${PREFIX}-finish`}
            >
              {words.finish}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
